import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { StatsTrendChart } from '../components/admin/StatsTrendChart';
import { adminApi } from '../api/admin';
import type { PlatformStats } from '../types/platformStats';
import { useLanguage } from '../i18n/useLanguage';
import { hasAnyRole } from '../auth/roleUtils';

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '0s';
  }
  const total = Math.round(seconds);
  if (total < 60) {
    return `${total}s`;
  }
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  if (minutes < 60) {
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

interface StatCardProps {
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <div className="admin-stat-card">
    <div className="admin-stat-card__value">{value}</div>
    <div className="admin-stat-card__label">{label}</div>
  </div>
);

interface StatsSectionProps {
  title: string;
  children: React.ReactNode;
}

const StatsSection: React.FC<StatsSectionProps> = ({ title, children }) => (
  <section className="admin-stats-section">
    <h2 className="admin-stats-section__title">{title}</h2>
    {children}
  </section>
);

const AdminStatisticsPageContent: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = hasAnyRole(user?.roles, 'ADMIN');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminApi.getPlatformStats();
        if (!cancelled) {
          setStats(data);
        }
      } catch {
        if (!cancelled) {
          setError(t('stats.failedLoad'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, navigate, t]);

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="page page--narrow">
      <h1 className="page-title">{t('stats.title')}</h1>
      <p className="page-description">{t('stats.description')}</p>
      <p className="page-description" style={{ marginTop: `calc(-1 * var(--spacing-md))`, fontSize: 'var(--font-size-sm)' }}>
        {t('stats.utcNote')}
      </p>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <LoadingSpinner />
      ) : stats ? (
        <>
          <StatsSection title={t('stats.sectionUsers')}>
            <div className="admin-stats-grid">
              <StatCard label={t('stats.registeredUsers')} value={stats.registeredUsersTotal} />
              <StatCard label={t('stats.loginsToday')} value={stats.loginsToday} />
              <StatCard label={t('stats.loginsMonth')} value={stats.loginsThisMonth} />
              <StatCard label={t('stats.loginsTotal')} value={stats.loginsTotal} />
              <StatCard label={t('stats.uniqueToday')} value={stats.uniqueUsersLoggedInToday} />
              <StatCard label={t('stats.uniqueMonth')} value={stats.uniqueUsersLoggedInThisMonth} />
              <StatCard label={t('stats.uniqueTotal')} value={stats.uniqueUsersLoggedInTotal} />
            </div>
            <StatsTrendChart
              title={t('stats.chartUsersLogins')}
              series={[
                {
                  label: t('stats.chartLogins'),
                  color: 'var(--color-primary)',
                  data: stats.loginTrend,
                },
                {
                  label: t('stats.chartUniqueUsers'),
                  color: 'var(--color-accent-dark)',
                  data: stats.uniqueLoginTrend,
                },
              ]}
            />
          </StatsSection>

          <StatsSection title={t('stats.sectionEngagement')}>
            <div className="admin-stats-grid">
              <StatCard
                label={t('stats.avgSession')}
                value={formatDuration(stats.averageSessionSeconds)}
              />
            </div>
            <StatsTrendChart
              title={t('stats.chartEngagement')}
              variant="line"
              lineData={stats.sessionDurationTrend}
              lineLabel={t('stats.chartAvgSession')}
              formatValue={(v) => formatDuration(v)}
            />
          </StatsSection>

          <StatsSection title={t('stats.sectionContent')}>
            <div className="admin-stats-grid">
              <StatCard label={t('stats.postsTotal')} value={stats.postsTotal} />
              <StatCard label={t('stats.postsToday')} value={stats.postsToday} />
              <StatCard label={t('stats.postsMonth')} value={stats.postsThisMonth} />
              <StatCard label={t('stats.commentsTotal')} value={stats.commentsTotal} />
              <StatCard label={t('stats.commentsToday')} value={stats.commentsToday} />
              <StatCard label={t('stats.commentsMonth')} value={stats.commentsThisMonth} />
            </div>
            <StatsTrendChart
              title={t('stats.chartContent')}
              series={[
                {
                  label: t('stats.chartPosts'),
                  color: 'var(--color-primary)',
                  data: stats.postsTrend,
                },
                {
                  label: t('stats.chartComments'),
                  color: 'var(--color-accent-dark)',
                  data: stats.commentsTrend,
                },
              ]}
            />
          </StatsSection>

          <StatsSection title={t('stats.sectionModeration')}>
            <div className="admin-stats-grid">
              <StatCard label={t('stats.deletedPosts')} value={stats.deletedPosts} />
              <StatCard label={t('stats.deletedComments')} value={stats.deletedComments} />
              <StatCard label={t('stats.moderationActions')} value={stats.moderationActionsTotal} />
            </div>
          </StatsSection>
        </>
      ) : null}
    </div>
  );
};

export const AdminStatisticsPage: React.FC = () => (
  <ProtectedRoute>
    <AdminStatisticsPageContent />
  </ProtectedRoute>
);
